import moment from "moment";
import { esc, printHtml, tableHtml } from "../utils/printDoc";
import { buildVitalSeries, isVitalLine } from "./VitalsGraphs";

// The Observation Results print, in the layout of the clinical one: the latest
// vitals, then the approved results by date, with anything outside its range
// marked. Only approved results are passed in; nothing else is printed.

const flagOf = (l) => {
  const f = String(l.abnormalFlags || "").trim();
  return f && f.toUpperCase() !== "N" ? f : "";
};

export function printResults({ lines = [], entries = [], clinic = {}, fromDate = null }) {
  const series = buildVitalSeries({ lines, entries, clinic });
  const latest = (a) => (a.length ? a[a.length - 1] : null);
  const w = latest(series.weight);
  const h = latest(series.height);
  const bmi = latest(series.bmi);
  const bp = latest(series.bp);
  const o2 = latest(series.oxygen);
  const hr = latest(series.heartRate);
  const t = latest(series.temperature);
  const when = (p) => (p && !p.fromRecord ? moment(p.ts).format("DD-MMM-YYYY") : "");

  const vitalsRows = [
    w && ["Weight", `${w.value.toFixed(1)} kg`, when(w)],
    h && ["Height", `${h.value.toFixed(2)} m`, when(h)],
    bmi && ["BMI (calculated)", `${bmi.value.toFixed(1)} kg/m²`, when(bmi)],
    bp && ["Blood pressure", `${bp.sys}/${bp.dia} mmHg`, when(bp)],
    o2 && ["Oxygen saturation", `${o2.value}%`, when(o2)],
    hr && ["Heart rate", `${hr.value} bpm`, when(hr)],
    t && ["Temperature", `${t.value} °C`, when(t)],
  ].filter(Boolean);

  const labs = lines
    .filter((l) => !isVitalLine(l))
    .filter((l) => !fromDate || moment(l.dateTimeofTransaction).isSameOrAfter(fromDate))
    .sort((a, b) => new Date(b.dateTimeofTransaction) - new Date(a.dateTimeofTransaction));

  const byDay = new Map();
  labs.forEach((l) => {
    const key = moment(l.dateTimeofTransaction).format("YYYY-MM-DD");
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(l);
  });

  const resultsHtml = byDay.size
    ? [...byDay.entries()]
        .map(([day, rows]) => `<h2>${esc(moment(day).format("dddd DD-MMM-YYYY"))}</h2>${tableHtml(
          ["Test", "Result", "Units", "Reference range", "Flag"],
          rows.map((l) => {
            const flag = flagOf(l);
            return [
              l.text || l.identifier || "",
              flag ? { html: `<span class="flag">${esc(l.observationValue)}</span>` } : l.observationValue,
              l.units || "",
              l.referenceRange || "",
              flag ? { html: `<span class="flag">${esc(flag)}</span>` } : "",
            ];
          }),
        )}`)
        .join("")
    : '<p class="muted">There are no approved results to print.</p>';

  const body = `
    <h2>Vitals</h2>
    ${vitalsRows.length ? tableHtml(["Vital", "Latest", "Recorded"], vitalsRows) : '<p class="muted">No vitals have been recorded.</p>'}
    ${resultsHtml}
    <p class="muted">Only results your clinic has approved are shown. A result marked in red is outside its reference range; your clinician will go through what it means for you.</p>`;
  return printHtml({ title: "Observation results", subtitle: `${byDay.size} dated result set${byDay.size === 1 ? "" : "s"}`, bodyHtml: body });
}
